import { lazy, Suspense, useEffect, useState } from 'react';
import './App.css';
import { AppHeader } from './components/AppHeader';
import { PropertyList } from './components/PropertyList';
import { useProperties } from './hooks/useProperties';
import { useUser } from './hooks/useUser';
import { signOut } from './api/auth';
import { resetToFreshAnonymousUser } from './db/repository';
import type { ViewState } from './app/viewState';
import type { User } from './db/types';

// Code-splitting: only the first screen (PropertyList) + header load up
// front. Every other view — and the heavy CONQUAS data it pulls in
// (PropertyDashboard / RoomWalkthrough / ReportView) — is split into its
// own chunk and fetched on demand. This keeps the initial JS parse small,
// which is what Lighthouse's Total Blocking Time (30% of the score) and
// LCP (25%) penalise. DefectForm rides along in the RoomWalkthrough chunk.
const PropertyForm = lazy(() =>
  import('./components/PropertyForm').then((m) => ({
    default: m.PropertyForm,
  })),
);
const PropertyDashboard = lazy(() =>
  import('./components/PropertyDashboard').then((m) => ({
    default: m.PropertyDashboard,
  })),
);
const ReportView = lazy(() =>
  import('./components/ReportView').then((m) => ({ default: m.ReportView })),
);
const RoomWalkthrough = lazy(() =>
  import('./components/RoomWalkthrough').then((m) => ({
    default: m.RoomWalkthrough,
  })),
);
const SignUpFlow = lazy(() =>
  import('./components/SignUpFlow').then((m) => ({ default: m.SignUpFlow })),
);

function App() {
  const {
    user: initialUser,
    loading: userLoading,
    error: userError,
  } = useUser();
  const [user, setUser] = useState<User | null>(null);
  const [view, setView] = useState<ViewState>({ kind: 'home' });
  const [signingOut, setSigningOut] = useState(false);
  const { properties, refresh: refreshProperties } = useProperties(
    user?.id ?? null,
  );

  // Sync the user state once on initial load.
  useEffect(() => {
    if (initialUser && !user) setUser(initialUser);
  }, [initialUser, user]);

  // URL-driven entry into the sign-in flow:
  //  - ?verify=TOKEN — the user is returning from a magic-link email.
  //  - ?signin / ?signup — a "Sign in" / "Use the app" CTA on the marketing
  //    site links straight here so the flow opens immediately (sign-in and
  //    sign-up share one magic-link path; the screen handles both).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsAuth =
      params.has('verify') || params.has('signin') || params.has('signup');
    if (wantsAuth && view.kind === 'home') {
      // Top-level "Sign in" links use sign-in copy; ?verify just resumes
      // an in-progress flow so its copy doesn't matter.
      const mode = params.has('verify') ? 'save' : 'signin';
      setView({ kind: 'sign-up', mode });
    }
    // Run only on first render — subsequent in-app navigation does not
    // need to react to URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (userLoading) {
    return (
      <main className="app">
        <p className="loading">Setting up your local store…</p>
      </main>
    );
  }
  if (userError || !user) {
    return (
      <main className="app">
        <p className="error" role="alert">
          Could not initialise the local data store. Please reload the page.
          {userError && (
            <span className="error-detail">{userError.message}</span>
          )}
        </p>
      </main>
    );
  }

  function handleBack() {
    if (view.kind === 'new-property') setView({ kind: 'home' });
    else if (view.kind === 'property') setView({ kind: 'home' });
    else if (view.kind === 'walkthrough')
      setView({ kind: 'property', propertyId: view.propertyId });
    else if (view.kind === 'report')
      setView({ kind: 'property', propertyId: view.propertyId });
    else if (view.kind === 'sign-up') setView({ kind: 'home' });
  }

  async function handleSignOut() {
    setSigningOut(true);
    try {
      // End the server session (clears the HttpOnly cookie + revokes the
      // KV record). Proceed even if this fails so the device is logged out.
      await signOut();
    } catch {
      /* ignore network errors — still drop the local session below */
    }
    // Repoint the local "current user" to a fresh anonymous record. The
    // account's data stays on the server and re-syncs on next sign-in.
    await resetToFreshAnonymousUser();
    // Hard reload to load the fresh anonymous user + clear all state.
    window.location.href = '/';
  }

  return (
    <div className="app">
      <AppHeader view={view} onBack={handleBack} />
      <main className="app-main">
        {view.kind === 'home' && (
          <PropertyList
            user={user}
            properties={properties}
            onAdd={() => setView({ kind: 'new-property' })}
            onOpen={(propertyId) => setView({ kind: 'property', propertyId })}
            onSignUp={() => setView({ kind: 'sign-up', mode: 'save' })}
            onAccountDeleted={() => {
              // Local DB already wiped by the panel. Reload to spin up a
              // fresh anonymous user and clear all in-memory state.
              window.location.href = '/';
            }}
          />
        )}
        <Suspense
          fallback={
            <p className="loading" role="status">
              Loading…
            </p>
          }
        >
          {view.kind === 'new-property' && (
            <PropertyForm
              user={user}
              onCreated={async (propertyId) => {
                await refreshProperties();
                setView({ kind: 'property', propertyId });
              }}
              onCancel={() => setView({ kind: 'home' })}
            />
          )}
          {view.kind === 'property' && (
            <PropertyDashboard
              user={user}
              propertyId={view.propertyId}
              onOpenRoom={(roomId) =>
                setView({
                  kind: 'walkthrough',
                  propertyId: view.propertyId,
                  roomId,
                })
              }
              onViewReport={() =>
                setView({ kind: 'report', propertyId: view.propertyId })
              }
              onBack={() => setView({ kind: 'home' })}
            />
          )}
          {view.kind === 'report' && (
            <ReportView user={user} propertyId={view.propertyId} />
          )}
          {view.kind === 'walkthrough' && (
            <RoomWalkthrough
              user={user}
              propertyId={view.propertyId}
              roomId={view.roomId}
            />
          )}
          {view.kind === 'sign-up' && (
            <SignUpFlow
              user={user}
              mode={view.mode}
              onCancel={() => setView({ kind: 'home' })}
              onDone={(upgraded) => {
                setUser(upgraded);
                setView({ kind: 'home' });
              }}
            />
          )}
        </Suspense>
      </main>
      <footer className="app-footer">
        <p>
          {user.isAnonymous
            ? 'pre-launch · all data stored locally on this device'
            : `signed in as ${user.email ?? 'your account'}`}
          {!user.isAnonymous && (
            <>
              {' · '}
              <button
                type="button"
                className="app-footer-signout"
                onClick={handleSignOut}
                disabled={signingOut}
              >
                {signingOut ? 'signing out…' : 'sign out'}
              </button>
            </>
          )}
        </p>
        <a
          className="app-footer-home"
          href="https://propdefect-site.davidung2104.workers.dev/"
        >
          ← Back to propdefect.com.sg
        </a>
      </footer>
    </div>
  );
}

export default App;
